import React, { ReactNode } from "react";
import { ButtonProps, Col, FormInstance, Modal as AntModal, notification, Row, Spin, Tooltip } from "antd";
import { Form, ModalProps } from "antd/es";
import { callAll, formatDate, getPeriodString } from "../utils/utils";
import useNormalizeData from "../utils/normalizedRecord";
import queryClient, { useMutation } from "components/utils/queryClient";
import { ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";
import RoleGuard from "components/standalone/role-guard";
import dayjs from "dayjs";
import { AccessControlTypes, JobFeedback, RoleGuardProps, Script } from "types";
import { useNavigate } from "react-router";
interface ModalContextProps {
  isOpen: boolean;
  isSaving: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  form: FormInstance<any>;
  onOk: (key: any, action: any, resource: any) => Promise<any>;
}

const ModalContext = React.createContext<ModalContextProps>(null);

function setTypeValue(type: string, values) {
  if (type === "simple") {
    return { cron: values.simple };
  }
  if (type === "cron") {
    return { cron: values.cron };
  }
  if (type === "continuous") {
    const hours = Math.floor(values.delay / 3600);
    const minutes = Math.floor(values.delay / 60);
    const seconds = values.delay % 60;

    return { delay: `0.${hours}:${minutes}:${seconds}.0000000` };
  }
  if (type === "oneTime") {
    let myUtc = formatDate(values.datetime);
    return { oneTime: `/Date(${dayjs(myUtc).valueOf()})/` };
  }
}

function Modal({ children, ...props }) {
  const [form] = Form.useForm();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const normalizedRecord = useNormalizeData();
  const navigate = useNavigate();

  const { mutateAsync } = useMutation<any>();

  const onOk = React.useCallback(
    async (key: any, action: any, resource: any) => {
      let values = await form.validateFields();

      if (key === "/tag" && action === "create") {
        values["color"] = values?.color?.hex;
      }

      if (key === "/trigger" && action === "update") {
        if (values?.eventType?.startsWith("Job")) {
          values["dashboard"] = null;
        }
        if (values?.eventType?.startsWith("Dashboard")) {
          values["script"] = null;
        }
        if (values?.eventType?.startsWith("Server")) {
          values["script"] = null;
          values["dashboard"] = null;
        }
      }

      if (key === "/apptoken/grant") {
        values["expiration"] = dayjs()
          .add(parseInt(values?.expiration), "days")
          .format("lll");
        resource.creation = dayjs().format("lll");
        values["identity"] = {
          name: values?.identity,
        };
      }

      if (key === "/schedule") {
        let newSchedule = {
          ...setTypeValue(values.type, values),
          continuous: values.type === "continuous" ? true : false,
          timeZoneString: Intl.DateTimeFormat().resolvedOptions().timeZone,
          parameters: queryClient
            .getQueryData<Script[]>("/script")
            ?.find((script) => script.fullPath === values.script)
            ?.scriptParameters.map((parameter) => ({
              name: parameter.name,
              value: values[parameter.name],
              type: parameter.type,
            })),
          credential: values?.credential,
          environment:
            values.environment === "Default" ? null : values?.environment,
          script: values.script,
          name: values.name,
          paused: values.paused
        };

        resource = newSchedule;
        values = {};
      }

      if (key === "/variable") {
        values["secret"] = values?.variableType === "secret";
      }

      if (
        resource?.resourceInfo?.schemaName === "jobfeedback" &&
        action === "update"
      ) {
        let newFeedback: JobFeedback = {
          ...resource,
          complete: true,
          data: values?.data,
        };
        resource = newFeedback;
        values = {};
      }

      if (resource?.resourceInfo?.schemaName === "ratelimit") {
        const { method, ...rest } = values;
        values = rest;
        values["period"] = getPeriodString(rest?.period);
        values["endpoint"] = `${method}:${rest?.endpoint}`;
      }

      if (resource?.resourceInfo?.schemaName === "script") {
        values["environment"] =
          values.environment === "Default" ? null : values.environment;
      }

      if (key === "/ratelimit" && action === "create") {
        const { method, ...rest } = values;
        values = rest;
        values["period"] = getPeriodString(rest?.period);
        values["endpoint"] = `${method}:${rest?.endpoint}`;
      }

      if (key === "/publishedfolder" && action === "create") {
        values["defaultDocument"] = [values?.defaultDocument];
      }

      if (key === "/dashboard") {
        if (action === "create") {
          let newDashboard = {
            ...values,
            disableAutoStart: !values.autoStart,
            environment:
              values.environment === "Default" ? null : values.environment,
            dashboardFramework: {
              id: values.dashboardFramework,
            },
          };
          resource = newDashboard;
          values = {};
        }
      }

      setIsSaving(true);

      await mutateAsync(
        { key, action, ...normalizedRecord({ ...resource, ...values }) },
        {
          onError: (error) => {
            setIsOpen(false);
            setIsSaving(false);
            notification.error({
              message: "Error",
              description: error.response.data,
              icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
            })
          },
          onSuccess: (data, variables) => {
            setIsSaving(false);
            setIsOpen(false);
            if (variables?.key === "/license") {
              queryClient.refetchQueries("/license/feature");
            }

            if (variables?.key === '/template/export') {
              window.location.href = `/api/v1/template/export/${data}`
            }

            if (values?.gotoJob) {
              navigate(`/admin/automation/jobs/${data}`);
            }
            if (values?.gotoScript) {
              navigate(`/admin/automation/scripts/${data.fullPath}`);
            }
            form.resetFields();
          },
        }
      );
    },
    [form, mutateAsync, navigate, normalizedRecord]
  );

  const value = React.useMemo(() => ({ isOpen, setIsOpen, form, onOk, isSaving }), [
    isOpen,
    setIsOpen,
    form,
    onOk,
    isSaving,
  ]);

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

type ModalOpenButtonProps = {
  children: any;
  requiredRoles: Array<string>;
  requiredAccessControls: AccessControlTypes;
} & Omit<ButtonProps, "resource"> &
  Omit<RoleGuardProps, "children">;

function ModalOpenButton({ children: child, ...props }: ModalOpenButtonProps) {
  const { setIsOpen } = React.useContext(ModalContext);
  return (
    <RoleGuard
      requiredRoles={props.requiredRoles}
      requiredAccessControls={props.requiredAccessControls}
      accessControls={props.accessControls}
    >
      {child?.props?.action === "edit" ? (
        <Tooltip title="Edit">
          {React.cloneElement(child, {
            type: child?.props?.type || "dashed",
            icon: child?.props?.icon || <PlusOutlined />,
            onClick: callAll(() => setIsOpen(true), child?.props?.onClick),
          })}
        </Tooltip>
      ) : (
        React.cloneElement(child, {
          type: child?.props?.type || "dashed",
          icon: child?.props?.icon || <PlusOutlined />,
          onClick: callAll(() => setIsOpen(true), child?.props?.onClick),
        })
      )}
    </RoleGuard>
  );
}

function ModalContentBase({
  ...props
}: ModalProps & {
  children?: ReactNode;
}) {
  const { isOpen, isSaving } = React.useContext(ModalContext);
  return (
    <AntModal
      {...props}
      visible={isOpen}
      confirmLoading={isSaving}
      destroyOnClose
      maskClosable
      closable
    />
  );
}

function ModalContent({ title, children, onClickOk, ...props }) {
  const { setIsOpen, form, isSaving } = React.useContext(ModalContext);

  return (
    <ModalContentBase
      {...props}
      title={title}
      centered={true}
      forceRender={false}
      onOk={callAll(onClickOk)}
      okButtonProps={{ htmlType: "submit", form: props.formName }}
      onCancel={callAll(
        () => setIsOpen(false),
        form.resetFields(),
        props?.onCancel
      )}
    >
      {isSaving ? <Row align="middle"><Col><Spin size="large" tip="Saving..." /></Col></Row> : children}
    </ModalContentBase>
  );
}

const useModalContext = () => React.useContext(ModalContext);

export {
  Modal,
  ModalContent,
  ModalContentBase,
  ModalOpenButton,
  useModalContext,
};
